import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { languageOptions, translations, type LanguageKey, type TranslationContent } from './translations';

type TranslationValue = string | number | boolean | null | TranslationValue[] | { [key: string]: TranslationValue };

interface LocalizationContextValue {
  language: LanguageKey;
  setLanguage: (language: LanguageKey) => void;
  t: <T = string>(key: string, variables?: Record<string, string | number>) => T;
  options: typeof languageOptions;
}

const LocalizationContext = createContext<LocalizationContextValue | undefined>(undefined);

const STORAGE_KEY = 'cooptest-language';

function getValue(source: TranslationContent, path: string[]): TranslationValue | undefined {
  return path.reduce<TranslationValue | undefined>((acc, segment) => {
    if (acc && typeof acc === 'object' && !Array.isArray(acc)) {
      return (acc as Record<string, TranslationValue>)[segment];
    }
    return undefined;
  }, source as unknown as TranslationValue);
}

function replacePlaceholders(text: string, variables?: Record<string, string | number>) {
  if (!variables) return text;
  return text.replace(/{{\s*(\w+)\s*}}/g, (_, name: string) => {
    const value = variables[name];
    return value === undefined || value === null ? '' : String(value);
  });
}

function formatValue(value: TranslationValue, variables?: Record<string, string | number>): TranslationValue {
  if (typeof value === 'string') {
    return replacePlaceholders(value, variables);
  }

  if (Array.isArray(value)) {
    return value.map(item => formatValue(item, variables));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce<Record<string, TranslationValue>>((acc, [key, entry]) => {
      acc[key] = formatValue(entry, variables);
      return acc;
    }, {});
  }

  return value;
}

export function LocalizationProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<LanguageKey>(() => {
    if (typeof window === 'undefined') {
      return 'en';
    }
    const stored = window.localStorage.getItem(STORAGE_KEY) as LanguageKey | null;
    if (stored && translations[stored]) {
      return stored;
    }
    return 'en';
  });

  const setLanguage = useCallback((next: LanguageKey) => {
    setLanguageState(prev => {
      const resolved = translations[next] ? next : prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, resolved);
      }
      return resolved;
    });
  }, []);

  const translate = useCallback(
    <T,>(key: string, variables?: Record<string, string | number>): T => {
      const segments = key.split('.');
      const primary = getValue(translations[language], segments);
      const fallback = language === 'en' ? undefined : getValue(translations.en, segments);
      const resolved = (primary ?? fallback ?? key) as TranslationValue;
      return formatValue(resolved, variables) as T;
    },
    [language]
  );

  const value = useMemo<LocalizationContextValue>(() => ({
    language,
    setLanguage,
    t: translate,
    options: languageOptions,
  }), [language, setLanguage, translate]);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
}

export function useLocalization() {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
}

export function useTranslation() {
  const { t } = useLocalization();
  return { t };
}
